import { required, email, minLength, sameAs } from "@vuelidate/validators";
import { ValidationRule } from "@vuelidate/core";

type Rules = Record<string, ValidationRule>;

const withMessage = (rule: ValidationRule, message: string): ValidationRule => {
  return {
    ...rule,
    $message: () => message,
  } as ValidationRule;
};

export const emailRules: Rules = {
  required: withMessage(required, "Email is required"),
  email: withMessage(email, "Please enter a valid email address"),
};

export const passwordRules: Rules = {
  required: withMessage(required, "Password is required"),
  minLength: withMessage(
    minLength(8),
    "Password must be at least 8 characters long"
  ),
};

export const nameRules: Rules = {
  required: withMessage(required, "Name is required"),
  minLength: withMessage(
    minLength(2),
    "Name must be at least 2 characters long"
  ),
};

export const userIdRules: Rules = {
  required: withMessage(required, "User ID is required"),
};

export const phoneNumberRules: Rules = {
  required: withMessage(required, "Phone Number is required"),
};

export const checkboxRules: Rules = {
  required: withMessage(required, "You must agree to the terms and conditions"),
};

export const messageRules: Rules = {
  required: withMessage(required, "Message is required"),
  minLength: withMessage(minLength(10), "Message must be at least 10 characters long"),
};

export interface SignupRules {
  firstName: Rules;
  lastName: Rules;
  email: Rules;
  password: Rules;
  agreeToTerms: Rules;
}

export const signupRules: SignupRules = {
  firstName: nameRules,
  lastName: nameRules,
  email: emailRules,
  password: passwordRules,
  agreeToTerms: checkboxRules,
};

export interface SigninRules {
  email: Rules;
  password: Rules;
}

export const signinRules: SigninRules = {
  email: emailRules,
  password: passwordRules,
};

export interface ForgotPasswordEmailRules {
  email: Rules;
}

export const forgotPasswordEmailRules: ForgotPasswordEmailRules = {
  email: emailRules,
};

export interface ResetPasswordRules {
  newPassword: Rules;
  confirmPassword: Rules;
}

export const resetPasswordRules = (password: string): ResetPasswordRules => ({
  newPassword: passwordRules,
  confirmPassword: {
    ...passwordRules,
    sameAsPassword: withMessage(sameAs(password), "Passwords must match"),
  },
});

export interface ContactUsRules {
  firstName: Rules;
  lastName: Rules;
  email: Rules;
  message: Rules;
}

export const contactUsRules: ContactUsRules = {
  firstName: nameRules,
  lastName: nameRules,
  email: emailRules,
  message: messageRules,
};

export interface DeleteAccountRules {
  firstName: Rules;
  lastName: Rules;
  email: Rules;
  userId: Rules;
  phoneNumber: Rules;
}

export const deleteAccountRules: DeleteAccountRules = {
  firstName: nameRules,
  lastName: nameRules,
  email: emailRules,
  userId: userIdRules,
  phoneNumber: phoneNumberRules,
};


export interface PaypalWithdrawInformationRules {
  paypalEmail: Rules;
}

export const paypalWithdrawInformationRules: PaypalWithdrawInformationRules = {
  paypalEmail: emailRules,
};